import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../contexts/AuthContext';
import { PageShell } from '../components/layout';

const ForgotPassword = () => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { resetPassword, authError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await resetPassword(email);
      
      if (result) {
        setError('');
        setSuccess(true);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError('Failed to send password reset email. Please try again.');
      setSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell hideChat kind="form" dots containerSx={{ py: { xs: 2, sm: 8 }, justifyContent: 'center' }}>
      <Box sx={{ mb: { xs: 2, sm: 4 }, textAlign: 'center' }}>
        <RouterLink to="/" style={{ textDecoration: 'none' }}>
          <Typography
            variant="h3"
            component="div"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              ...theme.modern.utils.gradientText('primary'),
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            {/* <HomeIcon sx={{ color: theme.modern.colors.modern.mainColor[500] }} /> */}
            Reset Your Password
          </Typography>
        </RouterLink>

        <Typography 
          variant="body1" 
          sx={{ 
            ...theme.modern.typography.modernSubtext 
          }}
        >
          Enter your email and we'll send you a link to reset your password
        </Typography>
      </Box>

      {authError && !success && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {authError}
        </Alert>
      )}

      <Paper
        variant="modern"
        elevation={0}
        sx={{
          p: { xs: 2, sm: 4 },
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        {success ? (
          <Box textAlign="center">
            <Alert severity="success" sx={{ mb: 3 }}>
              Password reset link sent! Check your email for instructions.
            </Alert>
            <Typography variant="body1" sx={{ mb: 3 }}>
              We've sent a password reset link to <strong>{email}</strong>. 
              Please check your inbox and follow the instructions to reset your password.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              If you don't receive an email within a few minutes, check your spam folder or try again.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/login')}
              startIcon={<ArrowBackIcon />}
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: '0.5rem',
                borderColor: 'rgba(0, 0, 0, 0.1)',
                color: '#1a2b4c',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.2)',
                  bgcolor: 'rgba(0, 0, 0, 0.01)',
                },
              }}
            >
              Back to login
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!error}
                  helperText={error}
                  variant="outlined"
                  disabled={isSubmitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.5rem',
                    },
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="gradient"
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    py: 1.5,
                  }}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Remember your password?{' '}
          <Link
            component={RouterLink}
            to="/login"
            variant="body2"
            underline="hover"
            sx={{ fontWeight: 600, color: 'primary.main' }}
          >
            Back to login
          </Link>
        </Typography>
      </Box>
    </PageShell>
  );
};

export default ForgotPassword;

